import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ShoppingBag, Search, Menu, X } from "lucide-react";
import { useCart } from "../../../context/CartContext";
import AnnouncementBar from "./AnnouncementBar";

/**
 * SRC-styled fixed header. Transparent on the home page (over the hero)
 * and turns solid-white as the user scrolls past 40px. Nav items come from
 * `config.categories` so each store type shows its real category slugs
 * (tees/hoodies for clothing vs serums/moisturizers for skincare).
 */
export default function Header({ config, basePath = "", transparent = false, announcementMessages }) {
  const brand = config?.brand || {};
  const accent = brand.accentColor || "#E60000";
  const brandName = brand.name || "Store";
  const brandDot = brand.dotColor || accent;
  const cart = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const categories = (config?.categories || []).slice(0, 6);
  // Iter-77 — the header respects the HeaderEditor's nav_items when set,
  // plus editable "Shop All" label / search placeholder / icon toggles.
  const headerCfg = config?.header_config || {};
  const headerCopy = config?.copy?.header || {};
  const customNav = Array.isArray(headerCfg.nav_items) && headerCfg.nav_items.length
    ? headerCfg.nav_items.map((n) => ({
        to: (n.link || "").startsWith("http") ? n.link : `${basePath}${n.link || "/"}`,
        label: n.label || "",
      }))
    : null;
  const navItems = customNav || [
    { to: `${basePath}/shop`, label: headerCopy.shopAllLabel || "Shop All" },
    ...categories.map((c) => ({ to: `${basePath}/shop?category=${c.key || c.slug}`, label: c.title || c.label })),
  ];
  const showSearch = headerCfg.show_search !== false;
  const showCart = headerCfg.show_cart !== false;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`${basePath}/shop?search=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setQ("");
    }
  };

  const headerBg = transparent && !scrolled
    ? "bg-transparent text-white border-transparent"
    : "bg-white/95 backdrop-blur-md text-black border-neutral-200";

  return (
    <header data-testid="site-header" className="fixed top-0 left-0 right-0 z-50">
      <AnnouncementBar messages={announcementMessages} />
      <div className={`border-b transition-colors duration-300 ${headerBg}`}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between">
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 -ml-2"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" strokeWidth={1.5} />
          </button>

          <Link to={`${basePath}/`} data-testid="logo-link" className="font-display text-2xl tracking-tighter">
            {brandName.toUpperCase()}<span style={{ color: brandDot }}>.</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6 text-[13px] uppercase tracking-[0.2em]">
            {navItems.map((n) => (
              <NavLink
                key={n.label}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `link-underline pb-1 ${isActive ? "font-semibold" : ""}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1">
            {showSearch && (
            <button
              data-testid="search-toggle"
              onClick={() => setSearchOpen((v) => !v)}
              className="p-2"
              aria-label="Search"
            >
              <Search className="w-5 h-5" strokeWidth={1.5} />
            </button>
            )}
            {showCart && (
            <button
              data-testid="cart-toggle"
              onClick={() => cart.setDrawerOpen(true)}
              className="relative p-2"
              aria-label="Open cart"
            >
              <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
              {cart.count > 0 && (
                <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 text-white text-[10px] font-semibold rounded-full min-w-[16px] h-4 px-1 grid place-items-center" style={{ background: accent }}>
                  {cart.count}
                </span>
              )}
            </button>
            )}
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-neutral-200 bg-white text-black">
            <form onSubmit={onSearch} className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-center gap-2">
              <Search className="w-4 h-4 text-neutral-500" strokeWidth={1.5} />
              <input
                data-testid="search-input"
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={headerCopy.searchPlaceholder || "Search products..."}
                className="flex-1 bg-transparent outline-none py-2 text-sm"
              />
              <button type="button" onClick={() => setSearchOpen(false)} className="p-1"><X className="w-4 h-4" strokeWidth={1.5} /></button>
            </form>
          </div>
        )}
      </div>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 bg-white text-black z-[60] flex flex-col" data-testid="mobile-menu">
          <div className="flex items-center justify-between h-16 px-4 border-b border-neutral-200">
            <Link to={`${basePath}/`} onClick={() => setMobileOpen(false)} className="font-display text-2xl tracking-tighter">
              {brandName.toUpperCase()}<span style={{ color: brandDot }}>.</span>
            </Link>
            <button onClick={() => setMobileOpen(false)} className="p-2"><X className="w-6 h-6" strokeWidth={1.5} /></button>
          </div>
          <nav className="p-6 flex flex-col gap-4 text-lg uppercase tracking-widest">
            {navItems.map((n) => (
              <Link key={n.label} to={n.to} onClick={() => setMobileOpen(false)}>{n.label}</Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
